import type {
  AppointmentId,
  LocationId,
  SchedulingResourceKind,
  SchedulingTimezone,
} from "./scheduling";

export type VisitId = string & { readonly __brand: "VisitId" };
export type VisitEventId = string & { readonly __brand: "VisitEventId" };
export type VisitPacketId = string & { readonly __brand: "VisitPacketId" };
export type VisitPacketPrintId = string & {
  readonly __brand: "VisitPacketPrintId";
};
export type BarcodeGrantId = string & { readonly __brand: "BarcodeGrantId" };
export type BarcodeTokenHash = string & {
  readonly __brand: "BarcodeTokenHash";
};

export const visitStatuses = [
  "SCHEDULED",
  "CHECKED_IN",
  "IN_PROGRESS",
  "COMPLETED",
  "CHECKOUT_PENDING",
  "CHECKED_OUT",
  "NO_SHOW",
  "CANCELLED",
] as const;
export type VisitStatus = (typeof visitStatuses)[number];

export const visitEventKinds = [
  "CHECKED_IN",
  "VISIT_STARTED",
  "VISIT_COMPLETED",
  "CHECKOUT_STARTED",
  "CHECKED_OUT",
  "NO_SHOW_RECORDED",
  "VISIT_CANCELLED",
] as const;
export type VisitEventKind = (typeof visitEventKinds)[number];

export const staffOperationsViews = [
  "HOME",
  "TIMELINE",
  "LIST",
  "RESOURCE_BOARD",
  "CHECK_IN_QUEUE",
  "CHECKOUT_QUEUE",
  "PRINT_QUEUE",
] as const;
export type StaffOperationsView = (typeof staffOperationsViews)[number];

export const visitReadinessStatuses = [
  "READY",
  "ACTION_REQUIRED",
  "NOT_REQUIRED",
  "UNKNOWN",
] as const;
export type VisitReadinessStatus = (typeof visitReadinessStatuses)[number];

export interface StaffVisitResource {
  readonly id: string;
  readonly kind: SchedulingResourceKind;
  readonly label: string;
}

export interface StaffVisitReadiness {
  readonly forms: VisitReadinessStatus;
  readonly consent: VisitReadinessStatus;
  readonly preparation: VisitReadinessStatus;
}

export interface StaffVisitSummary {
  readonly visitId: VisitId;
  readonly appointmentId: AppointmentId;
  readonly revision: number;
  readonly locationId: LocationId;
  readonly guestDisplayName: string;
  readonly startsAt: string;
  readonly endsAt: string;
  readonly timezone: SchedulingTimezone;
  readonly durationMinutes: number;
  readonly serviceLabel: string;
  readonly modalityLabels: readonly string[];
  readonly resources: readonly StaffVisitResource[];
  readonly readiness: StaffVisitReadiness;
  readonly setupRequirements: readonly string[];
  readonly equipmentRequirements: readonly string[];
  readonly operationalAlerts: readonly string[];
  readonly checkoutIndicator: "NOT_READY" | "READY" | "COMPLETE";
  readonly status: VisitStatus;
}

export interface VisitEventRecord {
  readonly id: VisitEventId;
  readonly visitId: VisitId;
  readonly kind: VisitEventKind;
  readonly fromStatus: VisitStatus;
  readonly toStatus: VisitStatus;
  readonly occurredAt: string;
  readonly revision: number;
}

export interface StaffOperationsSnapshot {
  readonly date: string;
  readonly generatedAt: string;
  readonly locationId: LocationId;
  readonly locationLabel: string;
  readonly locationTimezone: SchedulingTimezone;
  readonly visits: readonly StaffVisitSummary[];
  readonly events: readonly VisitEventRecord[];
}

export interface StaffOperationsQuery {
  readonly date: string;
  readonly locationId: LocationId;
  readonly locationTimezone: SchedulingTimezone;
  readonly appointmentId?: AppointmentId;
  readonly visitId?: VisitId;
  readonly view: StaffOperationsView;
}

export interface VisitTransitionRequest {
  readonly visitId: VisitId;
  readonly expectedRevision: number;
  readonly idempotencyKey: string;
  readonly correlationId: string;
  readonly kind:
    | "CHECK_IN"
    | "START"
    | "COMPLETE"
    | "BEGIN_CHECKOUT"
    | "CHECK_OUT"
    | "NO_SHOW";
}

export type VisitTransitionOutcome =
  | {
      readonly kind: "confirmed";
      readonly visit: StaffVisitSummary;
      readonly event: VisitEventRecord;
    }
  | {
      readonly kind: "conflict";
      readonly currentRevision: number;
      readonly currentStatus: VisitStatus;
    }
  | {
      readonly kind: "rejected";
      readonly reason:
        | "INVALID_TRANSITION"
        | "NOT_ASSIGNED"
        | "OUTSIDE_LOCATION"
        | "CHECKOUT_NOT_READY";
    };

export interface GuestVisitPacket {
  readonly schemaVersion: "visit-packet.v1";
  readonly packetId: VisitPacketId;
  readonly visitId: VisitId;
  readonly appointmentId: AppointmentId;
  readonly guestDisplayName: string;
  readonly startsAt: string;
  readonly endsAt: string;
  readonly timezone: SchedulingTimezone;
  readonly durationMinutes: number;
  readonly staffDisplayNames: readonly string[];
  readonly locationLabel: string;
  readonly roomLabel: string | null;
  readonly stationLabel: string | null;
  readonly modalityLabels: readonly string[];
  readonly setupRequirements: readonly string[];
  readonly generatedAt: string;
  readonly preparationStatus: VisitReadinessStatus;
  readonly readiness: StaffVisitReadiness;
  readonly accommodations: readonly string[];
  readonly operationalNotes: readonly string[];
  readonly checkInAt: string | null;
  readonly startedAt: string | null;
  readonly completedAt: string | null;
  readonly checkoutAt: string | null;
  readonly revision: number;
}

export const visitPacketCodeFormats = ["QR", "DATA_MATRIX"] as const;
export type VisitPacketCodeFormat = (typeof visitPacketCodeFormats)[number];

export interface VisitPacketPrintRequest {
  readonly visitIds: readonly VisitId[];
  readonly date: string;
  readonly locationId: LocationId;
  readonly printerProfileId: string;
  readonly format: VisitPacketCodeFormat;
}

export interface VisitPacketPrintAudit {
  readonly id: VisitPacketPrintId;
  readonly visitId: VisitId;
  readonly packetRevision: number;
  readonly printedByUserId: string;
  readonly printerProfileId: string;
  readonly printerDisplayLabel: string;
  readonly eventKind: "PRINT_DIALOG_OPENED";
  readonly format: VisitPacketCodeFormat;
  readonly printedAt: string;
  readonly expiresAt: string;
}

export const barcodeGrantScopes = [
  "STAFF_VISIT_OPEN",
  "STAFF_CHECK_IN",
  "STAFF_CHECKOUT",
  "GUEST_CHECKOUT_DOWNLOAD",
] as const;
export type BarcodeGrantScope = (typeof barcodeGrantScopes)[number];

export interface BarcodeGrantRecord {
  readonly id: BarcodeGrantId;
  readonly organizationId: string;
  readonly visitId: VisitId;
  readonly tokenHash: BarcodeTokenHash;
  readonly scopes: readonly BarcodeGrantScope[];
  readonly audience: "STAFF" | "GUEST";
  readonly hashVersion: "SHA256_V1";
  readonly revision: number;
  readonly expiresAt: string;
  readonly revokedAt: string | null;
  readonly revokedByUserId: string | null;
  readonly revocationReasonCode: string | null;
  readonly createdByUserId: string;
  readonly createdAt: string;
}

export interface IssuedBarcodeGrant {
  readonly grantId: BarcodeGrantId;
  readonly visitId: VisitId;
  readonly audience: "STAFF" | "GUEST";
  readonly scopes: readonly BarcodeGrantScope[];
  readonly expiresAt: string;
  readonly token: string;
  readonly resolverUrl: string;
}

export interface BarcodeResolution {
  readonly grantId: BarcodeGrantId;
  readonly visitId: VisitId;
  readonly audience: "STAFF" | "GUEST";
  readonly scopes: readonly BarcodeGrantScope[];
}

export const visitPacketPrintLimits = Object.freeze({
  maximumPacketsPerBatch: 100,
  maximumStaffNames: 4,
  maximumModalityLabels: 4,
  maximumSetupRequirements: 8,
  maximumAccommodations: 6,
  maximumOperationalNotes: 6,
  maximumGuestDisplayNameCharacters: 120,
  maximumLocationLabelCharacters: 120,
  maximumRoomLabelCharacters: 120,
  maximumStationLabelCharacters: 120,
  maximumStaffNameCharacters: 80,
  maximumModalityLabelCharacters: 80,
  maximumSetupRequirementCharacters: 160,
  maximumAccommodationCharacters: 160,
  maximumOperationalNoteCharacters: 180,
  maximumTotalVariableCharacters: 1400,
});

export const barcodeGrantControlKinds = ["REGENERATE", "REVOKE"] as const;
export type BarcodeGrantControlKind = (typeof barcodeGrantControlKinds)[number];

export interface BarcodeGrantControlRequest {
  readonly kind: BarcodeGrantControlKind;
  readonly grantId: BarcodeGrantId;
  readonly visitId: VisitId;
  readonly expectedRevision: number;
  readonly idempotencyKey: string;
  readonly correlationId: string;
}

const canonicalTimestampPattern =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;
const localDatePattern = /^\d{4}-\d{2}-\d{2}$/;
const safeIdentifierPattern = /^[A-Za-z0-9:_-]{3,160}$/;
const sha256Pattern = /^[a-f0-9]{64}$/;

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasExactKeys(
  value: Readonly<Record<string, unknown>>,
  keys: readonly string[],
): boolean {
  const actual = Object.keys(value).sort();
  const expected = [...keys].sort();
  return (
    actual.length === expected.length &&
    actual.every((key, index) => key === expected[index])
  );
}

function boundedText(value: unknown, maximum: number): value is string {
  return (
    typeof value === "string" &&
    value.trim() === value &&
    value.length > 0 &&
    value.length <= maximum
  );
}

function canonicalTimestamp(value: unknown): value is string {
  if (typeof value !== "string" || !canonicalTimestampPattern.test(value)) {
    return false;
  }
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) && new Date(parsed).toISOString() === value;
}

function localDate(value: unknown): value is string {
  if (typeof value !== "string" || !localDatePattern.test(value)) {
    return false;
  }
  const parsed = Date.parse(`${value}T00:00:00.000Z`);
  return (
    Number.isFinite(parsed) &&
    new Date(parsed).toISOString().slice(0, 10) === value
  );
}

function safeIdentifier(value: unknown): value is string {
  return typeof value === "string" && safeIdentifierPattern.test(value);
}

function schedulingTimezone(value: unknown): value is SchedulingTimezone {
  if (
    typeof value !== "string" ||
    value.length < 3 ||
    value.length > 64 ||
    /\s/u.test(value)
  ) {
    return false;
  }
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

function timestampLocalDate(
  timestamp: string,
  timezone: string,
): string | null {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      calendar: "gregory",
      day: "2-digit",
      month: "2-digit",
      numberingSystem: "latn",
      timeZone: timezone,
      year: "numeric",
    }).formatToParts(new Date(timestamp));
    const part = (type: "year" | "month" | "day") =>
      parts.find((item) => item.type === type)?.value;
    const year = part("year");
    const month = part("month");
    const day = part("day");
    return year && month && day ? `${year}-${month}-${day}` : null;
  } catch {
    return null;
  }
}

function boundedTextArray(
  value: unknown,
  maximumItems: number,
  maximumLength: number,
): value is readonly string[] {
  return (
    Array.isArray(value) &&
    value.length <= maximumItems &&
    value.every((item) => boundedText(item, maximumLength))
  );
}

function isVisitReadiness(value: unknown): value is StaffVisitReadiness {
  return (
    isRecord(value) &&
    hasExactKeys(value, ["consent", "forms", "preparation"]) &&
    ["forms", "consent", "preparation"].every((key) =>
      visitReadinessStatuses.includes(value[key] as VisitReadinessStatus),
    )
  );
}

export function isStaffVisitSummary(
  value: unknown,
): value is StaffVisitSummary {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      "appointmentId",
      "checkoutIndicator",
      "durationMinutes",
      "endsAt",
      "equipmentRequirements",
      "guestDisplayName",
      "locationId",
      "modalityLabels",
      "operationalAlerts",
      "readiness",
      "resources",
      "revision",
      "serviceLabel",
      "setupRequirements",
      "startsAt",
      "status",
      "timezone",
      "visitId",
    ])
  ) {
    return false;
  }
  if (
    !safeIdentifier(value.visitId) ||
    !safeIdentifier(value.appointmentId) ||
    !safeIdentifier(value.locationId) ||
    !boundedText(value.guestDisplayName, 120) ||
    !boundedText(value.serviceLabel, 160) ||
    !boundedText(value.timezone, 64) ||
    !canonicalTimestamp(value.startsAt) ||
    !canonicalTimestamp(value.endsAt) ||
    value.endsAt <= value.startsAt ||
    Date.parse(value.endsAt) - Date.parse(value.startsAt) !==
      Number(value.durationMinutes) * 60_000 ||
    !Number.isSafeInteger(value.durationMinutes) ||
    Number(value.durationMinutes) < 15 ||
    Number(value.durationMinutes) > 480 ||
    !Number.isSafeInteger(value.revision) ||
    Number(value.revision) < 1 ||
    !visitStatuses.includes(value.status as VisitStatus) ||
    !["NOT_READY", "READY", "COMPLETE"].includes(
      String(value.checkoutIndicator),
    ) ||
    !boundedTextArray(value.modalityLabels, 12, 120) ||
    !boundedTextArray(value.setupRequirements, 20, 240) ||
    !boundedTextArray(value.equipmentRequirements, 20, 160) ||
    !boundedTextArray(value.operationalAlerts, 20, 240) ||
    !Array.isArray(value.resources) ||
    value.resources.length > 20 ||
    !value.resources.every(
      (resource) =>
        isRecord(resource) &&
        hasExactKeys(resource, ["id", "kind", "label"]) &&
        safeIdentifier(resource.id) &&
        ["ROOM", "STATION", "STAFF", "EQUIPMENT", "SHARED_FACILITY"].includes(
          String(resource.kind),
        ) &&
        boundedText(resource.label, 160),
    ) ||
    !isVisitReadiness(value.readiness)
  ) {
    return false;
  }
  return true;
}

export function isStaffOperationsSnapshot(
  value: unknown,
): value is StaffOperationsSnapshot {
  if (!isRecord(value)) return false;
  const locationTimezone = value.locationTimezone;
  if (
    !hasExactKeys(value, [
      "date",
      "events",
      "generatedAt",
      "locationId",
      "locationLabel",
      "locationTimezone",
      "visits",
    ]) ||
    !localDate(value.date) ||
    !canonicalTimestamp(value.generatedAt) ||
    !safeIdentifier(value.locationId) ||
    !boundedText(
      value.locationLabel,
      visitPacketPrintLimits.maximumLocationLabelCharacters,
    ) ||
    !schedulingTimezone(locationTimezone) ||
    !Array.isArray(value.visits) ||
    value.visits.length > 500 ||
    !value.visits.every(isStaffVisitSummary) ||
    !Array.isArray(value.events) ||
    value.events.length > 2000 ||
    !value.events.every(isVisitEventRecord)
  ) {
    return false;
  }
  const visits = value.visits as readonly StaffVisitSummary[];
  const visitIds = new Set(visits.map((visit) => visit.visitId));
  return (
    visits.every(
      (visit) =>
        visit.locationId === value.locationId &&
        visit.timezone === locationTimezone &&
        timestampLocalDate(visit.startsAt, locationTimezone) === value.date,
    ) &&
    (value.events as readonly VisitEventRecord[]).every((event) =>
      visitIds.has(event.visitId),
    )
  );
}

export function isVisitEventRecord(value: unknown): value is VisitEventRecord {
  if (
    !(
      isRecord(value) &&
      hasExactKeys(value, [
        "fromStatus",
        "id",
        "kind",
        "occurredAt",
        "revision",
        "toStatus",
        "visitId",
      ]) &&
      safeIdentifier(value.id) &&
      safeIdentifier(value.visitId) &&
      visitEventKinds.includes(value.kind as VisitEventKind) &&
      visitStatuses.includes(value.fromStatus as VisitStatus) &&
      visitStatuses.includes(value.toStatus as VisitStatus) &&
      canonicalTimestamp(value.occurredAt) &&
      Number.isSafeInteger(value.revision) &&
      Number(value.revision) >= 1
    )
  ) {
    return false;
  }
  const transitionByKind: Readonly<
    Record<VisitEventKind, readonly [VisitStatus, VisitStatus]>
  > = {
    CHECKED_IN: ["SCHEDULED", "CHECKED_IN"],
    VISIT_STARTED: ["CHECKED_IN", "IN_PROGRESS"],
    VISIT_COMPLETED: ["IN_PROGRESS", "COMPLETED"],
    CHECKOUT_STARTED: ["COMPLETED", "CHECKOUT_PENDING"],
    CHECKED_OUT: ["CHECKOUT_PENDING", "CHECKED_OUT"],
    NO_SHOW_RECORDED: ["SCHEDULED", "NO_SHOW"],
    VISIT_CANCELLED: [value.fromStatus as VisitStatus, "CANCELLED"],
  };
  const [fromStatus, toStatus] = transitionByKind[value.kind as VisitEventKind];
  return value.fromStatus === fromStatus && value.toStatus === toStatus;
}

export function isGuestVisitPacket(value: unknown): value is GuestVisitPacket {
  return (
    isRecord(value) &&
    hasExactKeys(value, [
      "accommodations",
      "appointmentId",
      "checkInAt",
      "checkoutAt",
      "completedAt",
      "durationMinutes",
      "endsAt",
      "guestDisplayName",
      "locationLabel",
      "modalityLabels",
      "operationalNotes",
      "packetId",
      "preparationStatus",
      "readiness",
      "revision",
      "roomLabel",
      "schemaVersion",
      "setupRequirements",
      "staffDisplayNames",
      "startedAt",
      "startsAt",
      "stationLabel",
      "timezone",
      "visitId",
      "generatedAt",
    ]) &&
    safeIdentifier(value.packetId) &&
    safeIdentifier(value.visitId) &&
    safeIdentifier(value.appointmentId) &&
    value.schemaVersion === "visit-packet.v1" &&
    boundedText(
      value.guestDisplayName,
      visitPacketPrintLimits.maximumGuestDisplayNameCharacters,
    ) &&
    boundedText(
      value.locationLabel,
      visitPacketPrintLimits.maximumLocationLabelCharacters,
    ) &&
    (value.roomLabel === null ||
      boundedText(
        value.roomLabel,
        visitPacketPrintLimits.maximumRoomLabelCharacters,
      )) &&
    (value.stationLabel === null ||
      boundedText(
        value.stationLabel,
        visitPacketPrintLimits.maximumStationLabelCharacters,
      )) &&
    boundedTextArray(
      value.staffDisplayNames,
      visitPacketPrintLimits.maximumStaffNames,
      visitPacketPrintLimits.maximumStaffNameCharacters,
    ) &&
    boundedTextArray(
      value.modalityLabels,
      visitPacketPrintLimits.maximumModalityLabels,
      visitPacketPrintLimits.maximumModalityLabelCharacters,
    ) &&
    boundedTextArray(
      value.setupRequirements,
      visitPacketPrintLimits.maximumSetupRequirements,
      visitPacketPrintLimits.maximumSetupRequirementCharacters,
    ) &&
    boundedTextArray(
      value.accommodations,
      visitPacketPrintLimits.maximumAccommodations,
      visitPacketPrintLimits.maximumAccommodationCharacters,
    ) &&
    boundedTextArray(
      value.operationalNotes,
      visitPacketPrintLimits.maximumOperationalNotes,
      visitPacketPrintLimits.maximumOperationalNoteCharacters,
    ) &&
    [
      value.guestDisplayName,
      value.locationLabel,
      ...(value.roomLabel === null ? [] : [value.roomLabel]),
      ...(value.stationLabel === null ? [] : [value.stationLabel]),
      ...(value.staffDisplayNames as string[]),
      ...(value.modalityLabels as string[]),
      ...(value.setupRequirements as string[]),
      ...(value.accommodations as string[]),
      ...(value.operationalNotes as string[]),
    ].reduce((total, item) => total + item.length, 0) <=
      visitPacketPrintLimits.maximumTotalVariableCharacters &&
    isVisitReadiness(value.readiness) &&
    canonicalTimestamp(value.startsAt) &&
    canonicalTimestamp(value.endsAt) &&
    value.endsAt > value.startsAt &&
    Date.parse(value.endsAt) - Date.parse(value.startsAt) ===
      Number(value.durationMinutes) * 60_000 &&
    canonicalTimestamp(value.generatedAt) &&
    value.generatedAt <= value.endsAt &&
    [
      value.checkInAt,
      value.startedAt,
      value.completedAt,
      value.checkoutAt,
    ].every((item) => item === null || canonicalTimestamp(item)) &&
    visitReadinessStatuses.includes(
      value.preparationStatus as VisitReadinessStatus,
    ) &&
    Number.isSafeInteger(value.durationMinutes) &&
    Number(value.durationMinutes) >= 15 &&
    Number(value.durationMinutes) <= 480 &&
    Number.isSafeInteger(value.revision) &&
    Number(value.revision) >= 1
  );
}

export function isBarcodeGrantRecord(
  value: unknown,
): value is BarcodeGrantRecord {
  return (
    isRecord(value) &&
    hasExactKeys(value, [
      "createdAt",
      "createdByUserId",
      "expiresAt",
      "hashVersion",
      "id",
      "organizationId",
      "revision",
      "revokedAt",
      "revokedByUserId",
      "revocationReasonCode",
      "scopes",
      "tokenHash",
      "audience",
      "visitId",
    ]) &&
    [
      value.id,
      value.organizationId,
      value.visitId,
      value.createdByUserId,
    ].every(safeIdentifier) &&
    typeof value.tokenHash === "string" &&
    sha256Pattern.test(value.tokenHash) &&
    value.hashVersion === "SHA256_V1" &&
    Number.isSafeInteger(value.revision) &&
    Number(value.revision) >= 1 &&
    Array.isArray(value.scopes) &&
    value.scopes.length > 0 &&
    value.scopes.length <= 4 &&
    new Set(value.scopes).size === value.scopes.length &&
    value.scopes.every((scope) =>
      barcodeGrantScopes.includes(scope as BarcodeGrantScope),
    ) &&
    ["STAFF", "GUEST"].includes(String(value.audience)) &&
    canonicalTimestamp(value.expiresAt) &&
    canonicalTimestamp(value.createdAt) &&
    (value.revokedAt === null || canonicalTimestamp(value.revokedAt)) &&
    ((value.revokedAt === null &&
      value.revokedByUserId === null &&
      value.revocationReasonCode === null) ||
      (value.revokedAt !== null &&
        safeIdentifier(value.revokedByUserId) &&
        typeof value.revocationReasonCode === "string" &&
        /^[A-Z0-9_]{3,64}$/.test(value.revocationReasonCode)))
  );
}

export function isStaffOperationsQuery(
  value: unknown,
): value is StaffOperationsQuery {
  if (
    !isRecord(value) ||
    !["date", "locationId", "locationTimezone", "view"].every((key) =>
      Object.hasOwn(value, key),
    ) ||
    Object.keys(value).some(
      (key) =>
        ![
          "appointmentId",
          "date",
          "locationId",
          "locationTimezone",
          "view",
          "visitId",
        ].includes(key),
    )
  ) {
    return false;
  }
  return (
    localDate(value.date) &&
    safeIdentifier(value.locationId) &&
    schedulingTimezone(value.locationTimezone) &&
    (value.appointmentId === undefined ||
      safeIdentifier(value.appointmentId)) &&
    (value.visitId === undefined || safeIdentifier(value.visitId)) &&
    staffOperationsViews.includes(value.view as StaffOperationsView)
  );
}

export function isVisitPacketPrintRequest(
  value: unknown,
): value is VisitPacketPrintRequest {
  return (
    isRecord(value) &&
    hasExactKeys(value, [
      "date",
      "format",
      "locationId",
      "printerProfileId",
      "visitIds",
    ]) &&
    Array.isArray(value.visitIds) &&
    value.visitIds.length > 0 &&
    value.visitIds.length <= visitPacketPrintLimits.maximumPacketsPerBatch &&
    new Set(value.visitIds).size === value.visitIds.length &&
    value.visitIds.every(safeIdentifier) &&
    localDate(value.date) &&
    safeIdentifier(value.locationId) &&
    safeIdentifier(value.printerProfileId) &&
    visitPacketCodeFormats.includes(value.format as VisitPacketCodeFormat)
  );
}

export function isVisitPacketPrintAudit(
  value: unknown,
): value is VisitPacketPrintAudit {
  return (
    isRecord(value) &&
    hasExactKeys(value, [
      "eventKind",
      "expiresAt",
      "format",
      "id",
      "packetRevision",
      "printedAt",
      "printedByUserId",
      "printerDisplayLabel",
      "printerProfileId",
      "visitId",
    ]) &&
    safeIdentifier(value.id) &&
    safeIdentifier(value.visitId) &&
    Number.isSafeInteger(value.packetRevision) &&
    Number(value.packetRevision) >= 1 &&
    safeIdentifier(value.printedByUserId) &&
    safeIdentifier(value.printerProfileId) &&
    boundedText(value.printerDisplayLabel, 120) &&
    value.eventKind === "PRINT_DIALOG_OPENED" &&
    visitPacketCodeFormats.includes(value.format as VisitPacketCodeFormat) &&
    canonicalTimestamp(value.printedAt) &&
    canonicalTimestamp(value.expiresAt) &&
    value.expiresAt >= value.printedAt
  );
}

export function isVisitTransitionRequest(
  value: unknown,
): value is VisitTransitionRequest {
  return (
    isRecord(value) &&
    hasExactKeys(value, [
      "correlationId",
      "expectedRevision",
      "idempotencyKey",
      "kind",
      "visitId",
    ]) &&
    safeIdentifier(value.visitId) &&
    safeIdentifier(value.correlationId) &&
    safeIdentifier(value.idempotencyKey) &&
    Number.isSafeInteger(value.expectedRevision) &&
    Number(value.expectedRevision) >= 1 &&
    [
      "CHECK_IN",
      "START",
      "COMPLETE",
      "BEGIN_CHECKOUT",
      "CHECK_OUT",
      "NO_SHOW",
    ].includes(String(value.kind))
  );
}

export function isBarcodeGrantControlRequest(
  value: unknown,
): value is BarcodeGrantControlRequest {
  return (
    isRecord(value) &&
    hasExactKeys(value, [
      "correlationId",
      "expectedRevision",
      "grantId",
      "idempotencyKey",
      "kind",
      "visitId",
    ]) &&
    safeIdentifier(value.grantId) &&
    safeIdentifier(value.visitId) &&
    safeIdentifier(value.correlationId) &&
    safeIdentifier(value.idempotencyKey) &&
    Number.isSafeInteger(value.expectedRevision) &&
    Number(value.expectedRevision) >= 1 &&
    barcodeGrantControlKinds.includes(value.kind as BarcodeGrantControlKind)
  );
}
